<template>
    <auth-layout :isPlain="true" :isProgressBarHidden="true">
        <template #default>
            <common-content-wrapper width="large">
                <common-content-spacer height="super-huge">
                    <referral-details-card
                        v-show="showReferralDetails"
                        :showReferring="!isInsuranceObtained(referral) && !isInsuranceDeclined(referral)"
                        :showPatient="!isInsuranceObtained(referral) && !isInsuranceDeclined(referral)"
                        :referral="referral"
                        :heading="$t('pages.referrals.primary_care_insurance.view.referral_details')"
                        widgetsSize="small"
                    ></referral-details-card>

                    <common-page-header
                        v-if="isAppointmentInPast(referral)"
                        :heading="$t('pages.referrals.primary_care_insurance.view.referral_over')"
                        :isCenter="true"
                    ></common-page-header>

                    <insurance-auth-status
                        v-else-if="isInsuranceObtained(referral) || isInsuranceDeclined(referral)"
                        :isObtained="isInsuranceObtained(referral)"
                    ></insurance-auth-status>

                    <common-page-header
                        v-else-if="isReferralError(referral)"
                        :heading="$t('pages.referrals.primary_care_insurance.view.referral_cancelled')"
                        :isCenter="true"
                    ></common-page-header>

                    <common-content-spacer v-else-if="isReferralWaitingForInsurance(referral)" height="medium">
                        <common-icon v-if="isInsuranceJustObtained" name="success" size="ultra-large"></common-icon>

                        <common-page-header :heading="insuranceAuthTitle" :isCenter="true"></common-page-header>

                        <common-buttons v-if="showInsuranceActions">
                            <common-button
                                :label="$t('pages.referrals.show.insurance_modal.buttons.not_obtained')"
                                type="secondary"
                                @click="notObtainedYet"
                            ></common-button>
                            <common-button
                                :label="$t('pages.referrals.show.insurance_modal.buttons.declined')"
                                type="secondary"
                                @click="declineInsuranceAuth"
                            ></common-button>
                            <common-button
                                :label="$t('pages.referrals.show.insurance_modal.buttons.obtained')"
                                type="primary"
                                @click="showInsuranceAuthNumberForm"
                            ></common-button>
                        </common-buttons>

                        <common-content-wrapper v-else-if="isInsuranceAuthNumberFormOpen" width="medium">
                            <form-wrapper @submit="obtainInsuranceAuth(data.authNumber)">
                                <common-buttons class="primary-care-insurance__authorization-number">
                                    <text-field
                                        class="primary-care-insurance__authorization-number--label"
                                        :label="$t('pages.referrals.primary_care_insurance.view.auth_number_label')"
                                        :placeholder="$t('pages.referrals.primary_care_insurance.view.auth_number_placeholder')"
                                        :hasAutofocus="true"
                                        :formData="data"
                                        valuePath="authNumber"
                                        @change="change($event)"
                                    ></text-field>

                                    <common-button
                                        :label="$t('pages.referrals.primary_care_insurance.view.buttons.confirm')"
                                        type="primary"
                                    ></common-button>
                                </common-buttons>
                            </form-wrapper>
                        </common-content-wrapper>
                    </common-content-spacer>

                    <common-content-spacer v-if="showFooter" height="medium">
                        <p class="primary-care-insurance__footer">
                            {{ $t('pages.referrals.primary_care_insurance.view.footer') }}
                        </p>

                        <common-buttons>
                            <common-button
                                :label="$t('pages.referrals.primary_care_insurance.view.buttons.sign_up')"
                                type="primary"
                                :route="{ name: 'auth.signup' }"
                            ></common-button>
                        </common-buttons>
                    </common-content-spacer>
                </common-content-spacer>
            </common-content-wrapper>
        </template>

        <template #aside>
            <p>{{ $t('pages.auth.login.dont_have_account') }}</p>

            <common-button
                :route="{ name: 'auth.signup' }"
                :label="$t('pages.auth.login.get_started')"
                type="secondary"
                size="small"
            ></common-button>
        </template>
    </auth-layout>
</template>

<script>
    import AuthLayout from 'components/layouts/AuthLayout'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import FormMixin from 'mixins/FormMixin'
    import InsuranceAuthStatus from 'components/page-elements/referrals/insurance-auth/InsuranceAuthStatus'
    import ReferralDetailsCard from 'components/page-elements/referrals/details/ReferralDetailsCard'

    export default {
        mixins: [PageWithDataMixin, ReferralStatusMixin, ReferralConfirmMixin, FormMixin],

        components: {
            AuthLayout,
            InsuranceAuthStatus,
            ReferralDetailsCard,
        },

        data() {
            return {
                referral: {},
                signature: this.$route.params.signature,
                showInsuranceNotObtainedYet: false,
                isInsuranceJustCancelled: false,
                isInsuranceJustObtained: false,
                isInsuranceAuthNumberFormOpen: false,
            }
        },

        loadData(self) {
            let route = self.$route
            return {
                referral: ReferralsAPI.get(route.params.referralId, null, route.params.signature),
            }
        },

        computed: {
            insuranceAuthTitle() {
                if (this.isInsuranceJustCancelled) {
                    return this.$t('pages.referrals.primary_care_insurance.view.referral_cancelled')
                } else if (this.isInsuranceJustObtained) {
                    return this.$t('pages.referrals.primary_care_insurance.view.insurance_updated')
                } else if (this.isInsuranceAuthNumberFormOpen) {
                    return this.$t('pages.referrals.primary_care_insurance.view.enter_insurance_auth_number')
                } else if (this.showInsuranceNotObtainedYet) {
                    return this.$t('pages.referrals.primary_care_insurance.view.thanks_for_collaboration')
                }

                return this.$t('pages.referrals.primary_care_insurance.view.has_insurance_been_obtained')
            },

            showInsuranceActions() {
                return this.insuranceAuthNotChanged && !this.isInsuranceAuthNumberFormOpen
            },

            insuranceAuthNotChanged() {
                return !this.showInsuranceNotObtainedYet && !this.isInsuranceJustCancelled && !this.isInsuranceJustObtained
            },

            showFooter() {
                return (
                    this.showInsuranceNotObtainedYet ||
                    !this.isReferralWaitingForInsurance(this.referral) ||
                    this.isInsuranceJustCancelled ||
                    this.isInsuranceJustObtained
                )
            },

            showReferralDetails() {
                return (
                    (this.isReferralWaitingForInsurance(this.referral) ||
                        this.isInsuranceObtained(this.referral) ||
                        this.isInsuranceDeclined(this.referral)) &&
                    this.insuranceAuthNotChanged
                )
            },
        },

        methods: {
            loadReferral() {
                return ReferralsAPI.get(this.referral.id, null, this.signature).then((res) => (this.referral = res))
            },

            obtainInsuranceAuth(insuranceAuthNumber) {
                ReferralsAPI.updateInsuranceAuthorizationFromSignature(
                    this.referral.id,
                    'approve',
                    this.signature,
                    insuranceAuthNumber,
                )
                    .then(() => (this.isInsuranceJustObtained = true))
                    .catch(() => this.loadReferral())
                    .finally(() => (this.isInsuranceAuthNumberFormOpen = false))
            },

            declineInsuranceAuth() {
                ReferralsAPI.updateInsuranceAuthorizationFromSignature(this.referral.id, 'deny', this.signature)
                    .then(() => (this.isInsuranceJustCancelled = true))
                    .catch(() => this.loadReferral())
            },

            notObtainedYet() {
                this.showInsuranceNotObtainedYet = true
            },

            showInsuranceAuthNumberForm() {
                this.isInsuranceAuthNumberFormOpen = true
            },
        },
    }
</script>

<style lang="scss" scoped>
    .primary-care-insurance {
        &__footer {
            font-size: 18px;
            color: #333;
        }

        &__authorization-number {
            align-items: flex-end;
            &--label {
                width: 100%;
            }
        }
    }
</style>
